import React, { useMemo } from "react";
import { Box, alpha, useTheme } from "@mui/material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

// Colores para ownership
const COLORS = {
  mine: "#3B82F6",       // Azul — asignadas a mí
  delegated: "#F59E0B",  // Amber — delegadas a otros
};

// Colores por status
const STATUS_BORDER = {
  pending: "#D97706",
  in_progress: "#2563EB",
  completed: "#059669",
  cancelled: "#9CA3AF",
};

/**
 * Calendario de tareas con FullCalendar.
 * Colores por ownership (mine vs delegated) con borde por status.
 */
export default function TaskCalendar({ tasks, currentEmployeeId, onEventClick, onDateClick }) {
  const theme = useTheme();

  const events = useMemo(() => {
    return tasks
      .filter((t) => t.dueDate)
      .map((t) => {
        const isMine = String(t.assigneeId) === String(currentEmployeeId);
        const baseColor = isMine ? COLORS.mine : COLORS.delegated;
        const borderColor = STATUS_BORDER[t.status] || STATUS_BORDER.pending;
        const isCompleted = t.status === "completed";
        const isCancelled = t.status === "cancelled";

        return {
          id: String(t.id),
          title: t.title,
          start: t.dueDate,
          allDay: true,
          backgroundColor: isCompleted || isCancelled ? alpha(baseColor, 0.35) : baseColor,
          borderColor: borderColor,
          borderWidth: "3px",
          textColor: isCompleted || isCancelled ? "#6B7280" : "#FFFFFF",
          classNames: isCompleted ? ["fc-event-completed"] : [],
          extendedProps: {
            task: t,
            isMine,
            status: t.status,
            priority: t.priority,
            assigneeName: t.assigneeName,
            patientName: t.patientName,
          },
        };
      });
  }, [tasks, currentEmployeeId]);

  return (
    <Box
      sx={{
        "& .fc": {
          fontFamily: theme.typography.fontFamily,
        },
        "& .fc-toolbar-title": {
          fontSize: "1.25rem !important",
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
        "& .fc-button": {
          bgcolor: `${theme.palette.primary.main} !important`,
          border: "none !important",
          borderRadius: "8px !important",
          textTransform: "capitalize !important",
          fontWeight: "600 !important",
          fontSize: "0.8rem !important",
          padding: "6px 14px !important",
          boxShadow: "none !important",
          "&:hover": {
            bgcolor: `${theme.palette.primary.dark} !important`,
          },
          "&:focus": {
            boxShadow: "none !important",
          },
        },
        "& .fc-button-active": {
          bgcolor: `${theme.palette.primary.dark} !important`,
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)} !important`,
        },
        "& .fc-button-group": {
          gap: "4px",
          "& .fc-button": {
            borderRadius: "8px !important",
          },
        },
        "& .fc-daygrid-day": {
          transition: "background-color 0.2s ease",
          "&:hover": {
            bgcolor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
            "& .fc-daygrid-day-number": {
              color: theme.palette.primary.main,
            },
          },
        },
        "& .fc-daygrid-day-number": {
          fontWeight: 600,
          color: theme.palette.text.secondary,
          fontSize: "0.85rem",
          padding: "8px",
          transition: "color 0.2s ease",
        },
        "& .fc-day-today": {
          bgcolor: `${alpha("#764BA2", 0.07)} !important`,
          boxShadow: `inset 0 0 0 2px ${alpha("#764BA2", 0.35)}`,
          borderRadius: "4px",
          "& .fc-daygrid-day-number": {
            color: "#764BA2 !important",
            fontWeight: 800,
          },
        },
        "& .fc-event": {
          borderRadius: "6px !important",
          padding: "2px 6px !important",
          fontSize: "0.75rem !important",
          fontWeight: "600 !important",
          cursor: "pointer",
          transition: "transform 0.1s, box-shadow 0.1s",
          borderLeftWidth: "3px !important",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: `0 2px 8px ${alpha("#000", 0.15)}`,
          },
        },
        "& .fc-event-completed .fc-event-title": {
          textDecoration: "line-through",
        },
        "& .fc-col-header-cell": {
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          "& .fc-col-header-cell-cushion": {
            fontWeight: 700,
            fontSize: "0.8rem",
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            padding: "10px 4px",
          },
        },
        "& .fc-scrollgrid": {
          border: `1px solid ${theme.palette.divider} !important`,
          borderRadius: "12px",
          overflow: "hidden",
        },
        "& .fc-scrollgrid td, & .fc-scrollgrid th": {
          borderColor: `${alpha(theme.palette.divider, 0.6)} !important`,
        },
        "& .fc-list-event:hover td": {
          bgcolor: `${alpha(theme.palette.primary.main, 0.06)} !important`,
        },
        "& .fc-list-day-cushion": {
          bgcolor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
          fontWeight: 700,
        },
        "& .fc-daygrid-more-link": {
          color: theme.palette.primary.main,
          fontWeight: 700,
          fontSize: "0.75rem",
        },
      }}
    >
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,listWeek",
        }}
        events={events}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          onEventClick?.(info.event.extendedProps.task);
        }}
        dateClick={(info) => onDateClick?.(info.dateStr)}
        height="auto"
        dayMaxEvents={3}
        eventDisplay="block"
        locale="en"
        firstDay={0}
        buttonText={{
          today: "Today",
          month: "Month",
          week: "Week",
          list: "List",
        }}
        nowIndicator
      />
    </Box>
  );
}
